import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { superAdmin as api } from "@/lib/api/super";
import type {
  SuperEvent,
  SuperEventFilter,
  SuperEventPair,
  SuperEventWeek,
} from "@/lib/api/super";
import { useAuth } from "./useAuth";

export type {
  SuperEvent,
  SuperEventActor,
  SuperEventFilter,
  SuperEventPair,
  SuperEventTrackable,
  SuperEventWeek,
} from "@/lib/api/super";

export type SuperEventsStatus = "idle" | "loading" | "ready" | "error";

export const EVENTS_PER_PAGE = 50;

export interface SuperEventsContextValue {
  status: SuperEventsStatus;
  error: Error | null;
  events: readonly SuperEvent[];
  pairs: readonly SuperEventPair[];
  weeks: readonly SuperEventWeek[];
  total: number;
  page: number;
  pageCount: number;
  filter: SuperEventFilter;
  setFilter: (filter: SuperEventFilter) => void;
  setPage: (page: number) => void;
}

const SuperEventsContext = createContext<SuperEventsContextValue>(null!);

export function useSuperEvents(): SuperEventsContextValue {
  return useContext(SuperEventsContext);
}

function useSuperEventsProvider(): SuperEventsContextValue {
  const { user, status: authStatus } = useAuth();
  const [events, setEvents] = useState<readonly SuperEvent[]>([]);
  const [pairs, setPairs] = useState<readonly SuperEventPair[]>([]);
  const [weeks, setWeeks] = useState<readonly SuperEventWeek[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilterState] = useState<SuperEventFilter>({});
  const [status, setStatus] = useState<SuperEventsStatus>("idle");
  const [error, setError] = useState<Error | null>(null);

  const setFilter = useCallback((next: SuperEventFilter) => {
    setFilterState(next);
    setPage(1);
  }, []);

  useEffect(() => {
    if (authStatus !== "authenticated" || !user?.admin) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- load on auth/filter flip
    setStatus((s) => (s === "ready" ? s : "loading"));
    api
      .fetchSuperEvents({ ...filter, page, per_page: EVENTS_PER_PAGE })
      .then((res) => {
        if (cancelled) return;
        setEvents(res.events);
        setTotal(res.total);
        setStatus("ready");
        setError(null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [authStatus, user?.admin, filter, page]);

  useEffect(() => {
    if (authStatus !== "authenticated" || !user?.admin) return;
    let cancelled = false;
    api
      .fetchSuperEventMeta()
      .then((res) => {
        if (cancelled) return;
        setPairs(res.pairs);
        setWeeks(res.growth);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [authStatus, user?.admin]);

  const pageCount = Math.max(1, Math.ceil(total / EVENTS_PER_PAGE));

  return useMemo(
    () => ({
      status,
      error,
      events,
      pairs,
      weeks,
      total,
      page,
      pageCount,
      filter,
      setFilter,
      setPage,
    }),
    [
      status,
      error,
      events,
      pairs,
      weeks,
      total,
      page,
      pageCount,
      filter,
      setFilter,
    ],
  );
}

export function SuperEventsProvider({ children }: { children: ReactNode }) {
  const value = useSuperEventsProvider();
  return (
    <SuperEventsContext.Provider value={value}>
      {children}
    </SuperEventsContext.Provider>
  );
}
